import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FranchisePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    budget: '',
    experience: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Update page title and meta
    document.title = '加盟資訊 - Flan Cake 芙蘭蛋糕 | 完整品牌支援與專業培訓';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '芙蘭蛋糕加盟資訊，提供完整品牌支援、專業培訓課程、穩定供應鏈與行銷資源共享。加盟金50萬，總投資200-300萬，與我們一起將南美甜蜜帶給更多人。');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Flan Cake 加盟資訊",
      "description": "與我們一起將南美甜蜜帶給更多人",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/franchise`,
      "mainEntity": {
        "@type": "Service",
        "name": "Flan Cake 加盟服務",
        "provider": {
          "@type": "Organization",
          "name": "Flan Cake 芙蘭蛋糕"
        },
        "offers": {
          "@type": "Offer",
          "price": "500000",
          "priceCurrency": "TWD",
          "description": "加盟金"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Flan Cake 芙蘭蛋糕，源自南美洲的經典甜點，絲滑細膩的口感與濃郁焦糖香氣。提供線上購物、門市洽詢與加盟服務，將百年傳承的南美甜蜜帶到台灣。芙蘭蛋糕專賣店提供經典原味、咖啡、抹茶等多種口味，台北、台中、高雄、台南門市籌備中。');
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d66ong1i0m0fka7ok3b0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          city: '',
          budget: '',
          experience: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'ri-store-line',
      title: '完整品牌支援',
      description: '提供完整的品牌識別系統、裝潢設計與營運手冊',
    },
    {
      icon: 'ri-book-open-line',
      title: '專業培訓課程',
      description: '包含產品製作、門市管理、客戶服務等完整培訓',
    },
    {
      icon: 'ri-truck-line',
      title: '穩定供應鏈',
      description: '統一採購優質食材，確保產品品質與成本控制',
    },
    {
      icon: 'ri-megaphone-line',
      title: '行銷資源共享',
      description: '總部提供行銷素材、社群經營與廣告投放支援',
    },
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="px-6 mb-24">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-800 mb-6">
              加盟資訊
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              與我們一起將南美甜蜜帶給更多人
            </p>
          </div>
        </section>

        <section className="px-6 mb-24">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-lavender-600 text-white rounded-full mx-auto mb-6">
                    <i className={`${benefit.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 mb-24 bg-lavender-100 py-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif font-bold text-gray-800 mb-4">
                加盟條件
              </h2>
              <p className="text-gray-700">
                我們尋找有熱情、有理想的合作夥伴
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-lavender-600 text-white rounded-full flex-shrink-0 mt-1">
                    <i className="ri-money-dollar-circle-line"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">加盟金</h4>
                    <p className="text-gray-700">NT$ 500,000</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-lavender-600 text-white rounded-full flex-shrink-0 mt-1">
                    <i className="ri-home-line"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">店面需求</h4>
                    <p className="text-gray-700">20-30 坪，人潮聚集地段</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-lavender-600 text-white rounded-full flex-shrink-0 mt-1">
                    <i className="ri-wallet-line"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">總投資金額</h4>
                    <p className="text-gray-700">NT$ 2,000,000 - 3,000,000</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-lavender-600 text-white rounded-full flex-shrink-0 mt-1">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">合約期限</h4>
                    <p className="text-gray-700">3 年（可續約）</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif font-bold text-gray-800 mb-4">
                加盟諮詢表單
              </h2>
              <p className="text-gray-700">
                填寫以下資訊，我們將盡快與您聯繫
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-lg p-8 md:p-12" data-readdy-form id="franchise-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    姓名 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    電子郵件 *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    聯絡電話 *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    預計開店城市 *
                  </label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all cursor-pointer"
                  >
                    <option value="">請選擇</option>
                    <option value="台北市">台北市</option>
                    <option value="新北市">新北市</option>
                    <option value="桃園市">桃園市</option>
                    <option value="台中市">台中市</option>
                    <option value="台南市">台南市</option>
                    <option value="高雄市">高雄市</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                    預算範圍 *
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all cursor-pointer"
                  >
                    <option value="">請選擇</option>
                    <option value="200萬以下">200萬以下</option>
                    <option value="200-300萬">200-300萬</option>
                    <option value="300-500萬">300-500萬</option>
                    <option value="500萬以上">500萬以上</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                    餐飲經驗 *
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all cursor-pointer"
                  >
                    <option value="">請選擇</option>
                    <option value="無經驗">無經驗</option>
                    <option value="1-3年">1-3年</option>
                    <option value="3-5年">3-5年</option>
                    <option value="5年以上">5年以上</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  其他說明
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="請告訴我們您的想法或問題..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  感謝您的諮詢！我們將盡快與您聯繫。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  提交失敗，請稍後再試。
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-lavender-600 text-white py-4 rounded-lg font-medium hover:bg-lavender-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '提交中...' : '提交諮詢'}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FranchisePage;
